import { Info } from '@mui/icons-material';
import { Box } from '@mui/material';
import dayjs from 'dayjs';
import { getOwnCompanyProducts } from 'lib/api';
import { getSuppliers } from 'lib/api/publicMarket';
import { SuppliersResponse } from 'lib/api/publicMarket/types';
import GlobalSearcher, { Filter } from 'lib/common/GlobalSearcher';
import CustomButton from 'lib/designSystem/Button';
import TableWithPagination, {
  Header,
} from 'lib/designSystem/TableWithPagination';
import { errorHandler } from 'lib/functions/errors';
import { Product, PurchaseOrderType, TenderType } from 'lib/types/models';
import { Pagination } from 'lib/types/shared';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { showBusinessNetwork } from 'services/redux/businessNetworkSlice';

import GenerateLeadDialog from './Components/GenerateLeadDialog';

type Supplier = SuppliersResponse['data'][number];

const BASE_PAGINATION: Pagination = {
  page: 1,
  pageSize: 25,
  total: 0,
  totalPages: 0,
};

const Suppliers: React.FC = () => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [isSgr, setIsSgr] = useState(false);
  const [data, setData] = useState<SuppliersResponse['data']>([]);
  const [pagination, setPagination] = useState<Pagination>(BASE_PAGINATION);
  const [openLeadDialog, setOpenLeadDialog] = useState(false);
  const [selectedSuppliers, setSelectedSuppliers] = useState<Supplier[]>([]);
  const [tenderTypes, setTenderTypes] = useState<
    { value: TenderType; label: string }[]
  >([]);
  const [purchaseOrderTypes, setPurchaseOrderTypes] = useState<
    { value: PurchaseOrderType; label: string }[]
  >([]);

  const [selectedSupplierIdentifier, setSelectedSupplierIdentifier] =
    useState<string>('');
  const [initialDateStr, setInitialDateStr] = useState<string | null>(
    dayjs().subtract(30, 'day').format('YYYY-MM-DD'),
  );
  const [finalDateStr, setFinalDateStr] = useState<string | null>(
    dayjs().format('YYYY-MM-DD'),
  );

  const showSupplier = (id: string) => {
    const row = data.find((x: Supplier) => x.id === id);
    if (!row) return;
    dispatch(
      showBusinessNetwork({
        id: row.identifier,
        name: row.name,
      }),
    );
  };

  const handleSelectSupplier = (ids: (string | number)[]) => {
    const idsAsString = ids.map((id) => String(id));

    setSelectedSuppliers((prev) => {
      const suppliersNotInCurrentPage = prev.filter(
        (supplier) => !data.some((d) => d.identifier === supplier.identifier),
      );
      const suppliersFromCurrentPage = data.filter((supplier) =>
        idsAsString.includes(String(supplier.identifier)),
      );
      return [...suppliersNotInCurrentPage, ...suppliersFromCurrentPage];
    });
  };

  const selectedIds = selectedSuppliers.map((x: Supplier) => x.id);

  const filters: Filter[] = [
    {
      type: 'autocomplete',
      label: 'Proveedor',
      key: 'supplier',
      value: selectedSupplierIdentifier,
      setter: setSelectedSupplierIdentifier,
    },
    {
      type: 'date',
      label: 'Fecha inicio',
      dateKey: 'startDate',
      value: initialDateStr,
      setter: setInitialDateStr,
    },
    {
      type: 'date',
      label: 'Fecha fin',
      dateKey: 'endDate',
      value: finalDateStr,
      setter: setFinalDateStr,
    },
    {
      type: 'multiple',
      label: 'Tipo de licitación',
      value: tenderTypes,
      setter: setTenderTypes,
      options: Object.values(TenderType).map((t) => ({
        value: t,
        label: t,
      })),
    },
    {
      type: 'multiple',
      label: 'Tipo de orden',
      value: purchaseOrderTypes,
      setter: setPurchaseOrderTypes,
      options: Object.values(PurchaseOrderType).map((t) => ({
        value: t,
        label: t,
      })),
    },
  ];

  const buildSuppliersQuery = (override?: {
    page?: number;
    pageSize?: number;
  }): string => {
    const params = new URLSearchParams();
    if (selectedSupplierIdentifier)
      params.append('identifier', selectedSupplierIdentifier);
    if (initialDateStr) params.append('startDate', initialDateStr);
    if (finalDateStr) params.append('endDate', finalDateStr);
    const pageToUse = override?.page ?? pagination.page;
    const pageSizeToUse = override?.pageSize ?? pagination.pageSize;
    if (pageToUse) params.append('page', String(pageToUse));
    if (pageSizeToUse) params.append('pageSize', String(pageSizeToUse));
    if (tenderTypes.length > 0)
      params.append(
        'tenderTypes',
        tenderTypes.map((t) => String(t.value)).join(','),
      );
    if (purchaseOrderTypes.length > 0)
      params.append(
        'purchaseOrderTypes',
        purchaseOrderTypes.map((t) => String(t.value)).join(','),
      );
    return params.toString();
  };

  const fetchData = async (params?: {
    page: number;
    pageSize: number;
  }): Promise<{
    data: SuppliersResponse['data'];
    pagination: Pagination;
  } | void> => {
    try {
      setLoading(true);
      const pageToUse = params?.page ?? pagination.page;
      const pageSizeToUse = params?.pageSize ?? pagination.pageSize;
      const queryString = buildSuppliersQuery({
        page: pageToUse,
        pageSize: pageSizeToUse,
      });
      const response = await getSuppliers(dispatch, queryString);
      const nextData = response?.data ?? [];
      const nextPagination = response?.pagination ?? BASE_PAGINATION;
      setData(nextData);
      setPagination(nextPagination);
      return {
        data: nextData,
        pagination: nextPagination,
      };
    } catch (error) {
      errorHandler(dispatch, error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanyProducts = async () => {
    const availableProducts = await getOwnCompanyProducts(dispatch);
    setIsSgr(availableProducts.some((x: Product) => x.typeId === 'sgr'));
  };

  const handleSearch = async () => {
    await fetchData({
      page: BASE_PAGINATION.page,
      pageSize: BASE_PAGINATION.pageSize,
    });
  };

  useEffect(() => {
    void fetchData({
      page: BASE_PAGINATION.page,
      pageSize: BASE_PAGINATION.pageSize,
    });
    void fetchCompanyProducts();
  }, []);

  const disableLeadButton = selectedSuppliers.length === 0;

  const headers: Header[] = [
    { label: 'RUT', key: 'identifier' },
    {
      key: 'name',
      label: 'Emisor',
      handleAction: showSupplier,
      icon: <Info fontSize="small" />,
    },
    { label: 'Cant. Licit.', key: 'tenderCount' },
    {
      label: 'Monto Licit.',
      key: 'tenderAmount',
      type: 'money',
    },
    { label: 'Cant. Ordenes', key: 'purchaseOrderCount' },
    {
      label: 'Monto Ordenes',
      key: 'purchaseOrderAmount',
      type: 'money',
    },
    { label: 'F. Últ. Licit.', key: 'lastTenderDate', type: 'date' },
    { label: 'Cod. Últ. Licit.', key: 'lastTenderCode' },
    {
      label: 'F. Últ. Orden',
      key: 'lastPurchaseOrderDate',
      type: 'date',
    },
    { label: 'Cod. Últ. Orden', key: 'lastPurchaseOrderCode' },
    { label: 'Mis cuentas', key: 'isEnrolled', type: 'boolean-yes' },
  ];
  return (
    <>
      <Box sx={{ position: 'relative', py: 2 }}>
        <Box
          sx={{
            position: 'absolute',
            top: -36,
            right: 0,
            display: 'flex',
            gap: 2,
            alignItems: 'flex-start',
          }}
        >
          <GlobalSearcher
            filters={filters}
            triggerSearch={handleSearch}
            dispatch={dispatch}
          />
          <CustomButton
            color="primary"
            onClick={() => setOpenLeadDialog(true)}
            disabled={disableLeadButton}
          >
            Generar lead
          </CustomButton>
        </Box>
        <Box sx={{ pt: 2 }}>
          <TableWithPagination
            data={data}
            setData={setData}
            dataHeaders={headers}
            loading={loading}
            pagination={pagination}
            setPagination={setPagination}
            searchData={fetchData}
            exportToFile="xlsx"
            {...(isSgr && { selected: selectedIds })}
            {...(isSgr && { setSelected: handleSelectSupplier })}
            {...(isSgr && { cantSelectRow: (row: Supplier) => row.isEnrolled })}
          />
        </Box>
      </Box>
      <GenerateLeadDialog
        open={openLeadDialog}
        onClose={() => setOpenLeadDialog(false)}
        selectedSuppliers={selectedSuppliers}
        setSelectedSuppliers={setSelectedSuppliers}
      />
    </>
  );
};

export default Suppliers;
